import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  BreakPoint,
  Color,
  FontSize,
  Coordinates,
} from '../../styles/constants';
import Button from '../Button';
import CloseButton from '../CloseButton';

const Box = styled.div`
  background-color: ${Color.white};
  border-top: solid 1px ${Color.border.darkRed};
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;

  @media (min-width: ${BreakPoint.desktop}) {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
    height: ${props => 318 + props.headerHeight + 32}px;
    width: 456px;
    bottom: 32px;
    top: auto;
    left: auto;
    right: ${Coordinates.right};
  }
`;

// for Mobile Safari's bug
// https://qiita.com/chocolamint/items/67d25933880404c93db0
const BoxInner = styled.div`
  overflow-x: hidden;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  overflow-scrolling: touch;

  /* 640px - 72px - 18px */
  height: 550px;
  height: calc(100% - 72px);
  height: calc(100% - 72px - constant(safe-area-inset-bottom));
  height: calc(100% - 72px - env(safe-area-inset-bottom));

  @media (min-width: ${BreakPoint.desktop}) {
    overflow-y: hidden;
  }
`;

const Header = styled.div`
  background-color: ${Color.white};
  box-sizing: border-box;
  color: ${Color.text.black};
  height: ${props => props.headerHeight + 32}px;
  padding: 16px 16px 0 16px;
  margin-bottom: 16px;
  position: relative;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 1001;

  @media (min-width: ${BreakPoint.desktop}) {
    border-top-left-radius: 2px;
    border-top-right-radius: 2px;
    position: relative;
    width: 456px;
    margin-bottom: 0;
  }
`;

const HeaderBox = styled.div`
  box-sizing: border-box;
  height: ${props => props.headerHeight + 8}px;
  overflow: hidden;
`;

const HeadLine = styled.div`
  display: flex;
  align-items: start;
  justify-content: flex-end;
`;

const Close = styled.div`
  margin-left: 16px;
`;

const Message = styled.div`
  flex: 1;
  font-size: ${FontSize.small};
  margin-top: 8px;
`;

const Body = styled.div`
  border-top: solid 1px ${Color.border.ultraLightGray};
  box-sizing: border-box;
  position: relative;
  left: 0;
  width: 100%;
  z-index: 1000;

  @media (min-width: ${BreakPoint.desktop}) {
    overflow-y: scroll;
    position: relative;
    top: 0;
    height: 246px;
    width: 456px;
  }
`;

const BodyInner = styled.div`
  padding-bottom: 16px;
`;

const Field = styled.div`
  margin-top: 24px;
  padding-left: 16px;
  padding-right: 16px;

  &:first-child {
    margin-top: 16px;
  }

  @media (min-width: ${BreakPoint.desktop}) {
    margin-top: 12px;

    &:first-child {
      margin-top: 10px;
    }
  }
`;

const Footer = styled.div`
  border-top: solid 1px ${Color.border.ultraLightGray};
  background-color: ${Color.white};
  box-sizing: border-box;
  height: 72px;
  padding: 16px;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 1001;

  @media (min-width: ${BreakPoint.desktop}) {
    border-bottom-left-radius: 2px;
    border-bottom-right-radius: 2px;
    position: relative;
    width: 456px;
  }
`;

class FormField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      headerHeight: 0,
    };

    this.storedBodyPosition = document.body.style.position;
    this.storedBodyOverflow = document.body.style.overflow;
    this.storedScrollTop = null;
    this.scrollerRef = React.createRef();
    this.messageRef = React.createRef();
  }

  componentDidMount() {
    if (
      window.matchMedia(`(min-width:${BreakPoint.desktop})`).matches === false
    ) {
      this.storedScrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.position = 'fixed';
      document.body.style.overflow = 'hidden';
    }
    const messageNode = this.messageRef.current;
    let h = 10;
    if (messageNode) {
      h = messageNode.clientHeight;
    }
    this.setState({ headerHeight: h });
  }

  componentWillUnmount() {
    document.body.style.position = this.storedBodyPosition;
    document.body.style.overflow = this.storedBodyOverflow;
    if (
      window.matchMedia(`(min-width:${BreakPoint.desktop})`).matches === false
    ) {
      window.scrollTo(0, this.storedScrollTop);
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.forceScroll === false && this.props.forceScroll === true) {
      const scroller = this.scrollerRef.current;
      if (typeof scroller.scrollTo === 'function') {
        scroller.scrollTo(0, scroller.clientHeight);
      }
    }
  }

  render() {
    const {
      message,
      onActionClick,
      onCloseClick,
      actionLabel,
      children,
      className,
    } = this.props;

    return (
      <Box className={className} headerHeight={this.state.headerHeight}>
        <BoxInner>
          <Header headerHeight={this.state.headerHeight}>
            <HeaderBox headerHeight={this.state.headerHeight}>
              <HeadLine>
                {message && (
                  <Message innerRef={this.messageRef}>
                    {message.split('\n').map((item, key) => (
                      <span key={key}>
                        {item}
                        <br />
                      </span>
                    ))}
                  </Message>
                )}
                <Close>
                  <CloseButton color="red" onClick={onCloseClick} />
                </Close>
              </HeadLine>
            </HeaderBox>
          </Header>
          <Body>
            <BodyInner innerRef={this.scrollerRef}>{children}</BodyInner>
          </Body>
        </BoxInner>
        <Footer>
          <Button primary onClick={onActionClick}>
            {actionLabel}
          </Button>
        </Footer>
      </Box>
    );
  }
}

FormField.propTypes = {
  message: PropTypes.string,
  onActionClick: PropTypes.func.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  actionLabel: PropTypes.string.isRequired,
  forceScroll: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

FormField.defaultProps = {
  message: undefined,
  forceScroll: false,
};

export default FormField;

export { Field };



// WEBPACK FOOTER //
// ./src/components/SurveyToast/subComponents/FormField/index.jsx